import type { ApiErrorBody } from './contracts'
import { ApiError, NetworkError, ShellInterceptedError } from './errors'

const BASE = '/api/v1'

const TOKEN_KEY = 'ps.accessToken'

type SessionExpiredHandler = () => void

let onSessionExpired: SessionExpiredHandler | null = null

/**
 * AuthProvider registers the handler once. Every 401 session_expired routes through it,
 * so the JWT is cleared and login shown from exactly one place — and the outbox is
 * never touched on that path.
 */
export function setSessionExpiredHandler(handler: SessionExpiredHandler | null): void {
  onSessionExpired = handler
}

export function readToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY)
  } catch {
    // Private mode or blocked site data. Treat as signed out rather than crashing.
    return null
  }
}

export function writeToken(token: string | null): void {
  try {
    if (token === null) {
      localStorage.removeItem(TOKEN_KEY)
    } else {
      localStorage.setItem(TOKEN_KEY, token)
    }
  } catch {
    // Nothing to do — the session simply will not survive a reload.
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'DELETE'
  body?: unknown
  signal?: AbortSignal
  /** Login and health carry no token. */
  anonymous?: boolean
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, signal, anonymous = false } = options

  const headers: Record<string, string> = { Accept: 'application/json' }

  if (body !== undefined) {
    headers['Content-Type'] = 'application/json'
  }

  if (!anonymous) {
    const token = readToken()
    if (token) {
      headers.Authorization = `Bearer ${token}`
    }
  }

  const url = `${BASE}${path}`

  let response: Response
  try {
    response = await fetch(url, {
      method,
      headers,
      // DELETE carries a body on the items route. The spec specifies it and fetch
      // handles it; only System.Net.Http.Json needed special handling.
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
      cache: 'no-store',
    })
  } catch (cause) {
    if (signal?.aborted) {
      throw cause
    }
    throw new NetworkError(cause)
  }

  // An /api path that answers with HTML means the service worker served the SPA shell.
  // Surfacing it loudly is the only way that bug gets found before the aisle.
  const contentType = response.headers.get('content-type') ?? ''
  if (contentType.includes('text/html')) {
    throw new ShellInterceptedError(url)
  }

  if (response.status === 204) {
    return undefined as T
  }

  if (!response.ok) {
    const error = new ApiError(response.status, await readErrorBody(response))

    if (error.isSessionExpired) {
      onSessionExpired?.()
    }

    throw error
  }

  return (await response.json()) as T
}

async function readErrorBody(response: Response): Promise<ApiErrorBody> {
  try {
    const body = (await response.json()) as Partial<ApiErrorBody>
    if (typeof body?.code === 'string' && typeof body?.message === 'string') {
      return body as ApiErrorBody
    }
  } catch {
    // Fall through to the generic envelope below.
  }

  return {
    code: 'unexpected_response',
    message: 'پاسخ نامعتبر از سرور دریافت شد.',
    detail: `HTTP ${response.status}`,
  }
}
